import { useRef } from "react";
import FallbackPaywall from "@blocks/FallbackPaywall";
import DynamicImu from "@components/Advertisement/DynamicImu";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import DelayedComponentsLocationChange from "@components/DelayedComponents/DelayedComponentsLocationChange";
import ProgressBar from "@components/ProgressBar/ProgressBar";
import { useSetAdTargets } from "@features/advertisement/hooks/useSetAdTargets";
import PromoteBTGController from "@features/banner/PromoteBTG/controller/PromoteBTGController";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import PosthogFeature from "@features/posthog/PosthogFeature";
import {
  FEATURE_FLAG_DEEPERDIVE_AB_TEST,
  FEATURE_FLAG_DEEPERDIVE_AB_TEST_VARIANT,
} from "@features/posthog/utils/constants";
import TrendingCards from "@features/pulse/components/trendingCards/TrendingCards";
import Deeperdive from "@features/taboola/Deeperdive";
import useBypassDeeperdive from "@features/taboola/hooks/useBypassDeeperdive";
import Taboola from "@features/taboola/Taboola";
import ArticleToolbar from "@features/toolbar/ArticleToolbar/ArticleToolbar";
import ArticleGooglePreferedSourceButton from "@features/toolbar/ArticleToolbar/components/ArticleGooglePreferedSourceButton";
import PromoteCNTranslation from "@features/translations/PromoteCNTranslation";
import { useCountry } from "@hooks/useCountry";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useInjectClientData } from "@hooks/useInjectClientData";
import useIsAppWebView from "@hooks/useIsAppWebView";
import usePublishedTime from "@hooks/usePublishedTime";
import { useWindowSize } from "@hooks/useWindowSize";
import ArticleAttachments from "@routes/Article/components/ArticleAttachments";
import ArticleByline from "@routes/Article/components/ArticleByline/ArticleByline";
import ArticleKicker from "@routes/Article/components/ArticleKicker";
import ArticleParagraphElements from "@routes/Article/components/ArticleParagraphElements/ArticleParagraphElements";
import ArticlePurchaseLink from "@routes/Article/components/ArticlePurchaseLink/ArticlePurchaseLink";
import { Versions } from "@routes/Article/components/ArticlePurchaseLink/types";
import ArticleSubscriberLabel from "@routes/Article/components/ArticleSubscriberLabel";
import ArticleSubshareSent from "@routes/Article/components/ArticleSubshare/ArticleSubshareSent";
import ArticleTags from "@routes/Article/components/ArticleTags";
import { useHookUpdateUrlArticle } from "@routes/Article/hooks/useHookUpdateUrlArticle";
import {
  ArticleAuthorType,
  ArticlePaidModeType,
} from "@routes/Article/utils/enums";
import {
  getAdSettings,
  getCdpPaywallModuleId,
  getGAData,
  getIsBrandedContent,
  getTruncatedBody,
  getUrlPathFromAbsoluteUrl,
  isArticleExcludedFromPaywallBySection,
} from "@routes/Article/utils/helpers";
import { CommonArticleLayoutProps } from "@routes/Article/utils/types";
import { useSetReadingHistory } from "@routes/MyAccount/hooks/useSetReadingHistory";
import { IS_PLAYWRIGHT } from "@util/constant";
import { checkIfFromPaidPressRelease, cn } from "@util/helpers";
import { useFeatureFlagEnabled } from "posthog-js/react";

const InfographicNews = ({
  article,
  index = 0,
  gsChannels,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  alacrityRes,
  isModalOpen = false,
  setArticleObj,
  articleInView,
  displayArticleRef,
  isGifted = false,
  isPreview = false,
  isNavigate = false,
  isChinese = false,
  gaVariant,
  isTaboolaEnabled = false,
  trendingCards,
}: CommonArticleLayoutProps): React.ReactElement => {
  const { isBypassEnabled } = useBypassDeeperdive();
  const { isSubscriber, isAnonymous } = useUserInfo();

  const isUserLoginEnabled = useFeatureFlagEnabled("feature_enable_user_login");
  const isFeature60DaysAgo = useFeatureFlagEnabled("feature_60_days_ago");
  const isFeature30DaysAgo = useFeatureFlagEnabled(
    "feature_30_days_registration_wall"
  );
  const { isMoreThan60DaysAgo, isMoreThan30DaysAgo } = usePublishedTime(
    article?.publishTime
  );

  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    kicker,
    paidMode,
    publishTime,
    editedTime,
    hasUpdatedTimeDisplayed,
    relatedAttachments,
    sections,
    tags,
    copyright,
    standfirst,
    sentiment,
    availableTranslations,
    authors,
  } = article;
  useSetReadingHistory({ articleId: id });
  const { isScreenMD } = useWindowSize();

  const urlPath = getUrlPathFromAbsoluteUrl(article.urlPath || "");

  const { ref } = useHookUpdateUrlArticle(
    urlPath,
    isNavigate,
    article,
    isChinese,
    gaVariant,
    setArticleObj
  );
  const { gsChannels: gsChannelsAPI } = useGrapeshotData({
    urlPath: urlPath,
    index,
    keywords: sentiment?.keywords,
  });

  const isAppWebView = useIsAppWebView();
  const { isSGUser } = useCountry();

  const isPremium = paidMode === ArticlePaidModeType.PREMIUM;
  const mainSection = sections?.[0];

  const articleContentProgress = useRef<HTMLDivElement>(null);

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");
  const isPaidPressRelease = checkIfFromPaidPressRelease(
    article?.sections || []
  );

  const purchaseArticleDetails = {
    isDisplayed: copyright === "SPH",
    publishedDate: publishTime,
    title,
  };

  const isTruncated60DaysAgo =
    isFeature60DaysAgo && !isSubscriber && !isPremium && isMoreThan60DaysAgo;

  const isTruncated30DaysAgo =
    isFeature30DaysAgo && isAnonymous && !isPremium && isMoreThan30DaysAgo;

  const isFullContent =
    !isSGUser ||
    isSubscriber || // User is a subscriber
    isGifted || // Article is gifted
    isPreview || // Article is in preview mode
    !isUserLoginEnabled || // Feature flag is disabled
    isArticleExcludedFromPaywallBySection(sections) ||
    isBrandedContent || // Article is branded content
    (!(isTruncated60DaysAgo || isTruncated30DaysAgo) && !isPremium); // User is not affected by registration wall and article is not premium;

  const cdpPaywallModuleId = getCdpPaywallModuleId({
    isTruncated30DaysAgo: isTruncated30DaysAgo || false,
    isTruncated60DaysAgo: isTruncated60DaysAgo || false,
  });

  const partialElements = getTruncatedBody(article.body, 2);
  const bodyElements = isFullContent ? article.body : partialElements;

  const paragraphIndices = bodyElements
    .map((content, idx) => (content.startsWith("<p>") ? idx : null))
    .filter((idx) => idx !== null);
  const totalParagraphs = paragraphIndices.length;

  const isAuthorOnly = !authors?.some(
    (author) => author.type === ArticleAuthorType.CONTRIBUTOR_PROFILE
  );

  // Get initial ad targets from article data
  const adSettings = getAdSettings(article);
  const initialAdTargets = adSettings?.adTargets;

  // Set client-side ad targets while preserving server-side configuration
  useSetAdTargets({
    initialAdTargets,
    adTargets: [{ key: "ab_variant", value: gaVariant }],
  });

  useInjectClientData({ gaProps: getGAData(article, isChinese), gaVariant });

  return (
    <>
      <ArticleSubshareSent
        alacrityRes={alacrityRes}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <ProgressBar
        articleContentProgress={articleContentProgress}
        className={cn("top-[60px]")}
        isDisplayed={!isAppWebView && articleInView.id === article.id}
      />

      <article
        className={cn(
          "display-type--infographicNews w-full py-5",
          "animate-slideIn"
        )}
        data-index={index}
        data-cueid={id}
        data-url-path={urlPath}
        ref={ref}
      >
        <div ref={articleContentProgress} className="relative">
          <div className="px-3 md:px-0 md:pt-4">
            <div className="mx-auto mb-4 w-full max-w-[760px]">
              <>
                {isPremium || kicker ? (
                  <div className="flex flex-wrap items-center pb-2 md:px-8 md:pb-4">
                    <ArticleKicker
                      kicker={kicker}
                      className="font-base font-light uppercase"
                    />

                    {isPremium && kicker ? (
                      <div
                        className="ml-3 mr-2 font-poppins text-2xl font-light leading-normal tracking-[0.075rem] text-gray-650"
                        data-testid="kicker-subscriber-label-separator"
                      >
                        ·
                      </div>
                    ) : null}

                    <ArticleSubscriberLabel
                      isPremium={isPremium}
                      displayDot={false}
                      rootClassName={"font-base font-light"}
                    />
                  </div>
                ) : null}
              </>

              <h1
                data-testid="article-title"
                className="mb-2 font-lct text-4xl font-bold !leading-[1.3] tracking-[-0.045rem] text-gray-850 md:px-8 md:text-10xl"
              >
                {title}
              </h1>

              <>
                {standfirst ? (
                  <p
                    className={cn(
                      "mb-4 mt-2 font-lucida text-lg font-medium !leading-8 -tracking-5% text-gray-850 md:px-8 md:text-xl"
                    )}
                  >
                    {standfirst}
                  </p>
                ) : null}
              </>

              <>
                {!isAppWebView ? (
                  <div className="md:px-8">
                    <ArticleToolbar
                      shareUrl={article.shortUrl || ""}
                      articleTitle={title}
                      isPremium={isPremium}
                      handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                      setIsModalOpen={setIsModalOpen}
                      alacrityRes={alacrityRes}
                      displayPurchase={purchaseArticleDetails}
                      keyword={tags}
                      cueId={id}
                      isChinese={isChinese}
                      articleUrl={article.urlPath}
                    />

                    {isScreenMD ? (
                      <div className="mb-6 h-[1px] w-full bg-gray-175" />
                    ) : null}
                  </div>
                ) : null}
              </>

              <div ref={displayArticleRef} />

              <>
                {!isScreenMD ? (
                  <div className="border-t border-gray-175">
                    <ArticleGooglePreferedSourceButton
                      className="my-4 px-4 py-3"
                      articleUrl={article.urlPath}
                    />
                  </div>
                ) : null}
              </>

              <>
                {authors && isAuthorOnly ? (
                  <div
                    className={cn(
                      "md:flex md:flex-wrap md:space-y-0",
                      "relative mb-4 min-h-[50px] text-gray-850 md:mb-6 md:px-8",
                      "md:before:left-8 md:before:right-8"
                    )}
                  >
                    <ArticleByline
                      authors={authors}
                      editedTime={editedTime}
                      updatedTime={publishTime}
                      hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                      linkParams={
                        new URLSearchParams({ ref: "article-top-byline" })
                      }
                    />
                  </div>
                ) : null}
              </>

              <>
                {!isAuthorOnly ? (
                  <div className="mb-6 pt-6 md:px-8">
                    <ArticleByline
                      authors={authors || []}
                      editedTime={editedTime}
                      updatedTime={publishTime}
                      hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                    />
                  </div>
                ) : null}
              </>

              <>
                {!isChinese ? (
                  <PromoteCNTranslation
                    className="mb-6 md:px-8"
                    isTranslationAvailable={availableTranslations?.includes(
                      "zh-hans"
                    )}
                    link={urlPath}
                  />
                ) : null}
              </>

              <ArticleAttachments
                attachments={relatedAttachments || []}
                className="mb-6 md:mx-8"
              />
            </div>

            <div
              className={cn(
                "mx-auto mb-4 w-full px-0 font-lucida text-xl leading-8 -tracking-5% text-gray-850",
                "md:mb-6 md:max-w-[1200px] md:px-8",
                {
                  "mb-4 md:mb-6 [&>*:last-child]:relative [&>*:last-child]:after:absolute [&>*:last-child]:after:inset-0 [&>*:last-child]:after:bg-gradient-to-t [&>*:last-child]:after:from-white [&>*:last-child]:after:to-transparent":
                    !isFullContent,
                }
              )}
              data-testid="article-body-container"
            >
              <ArticleParagraphElements
                body={bodyElements || []}
                article={article}
                adsWrapperClass={cn(
                  "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                  { "md:!my-12": IS_PLAYWRIGHT }
                )}
                gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
                articleIndex={index}
                isGifted={isGifted}
                className={"mx-auto mb-4 md:mb-6"}
                isBrandedContent={isBrandedContent}
                isPaidPressRelease={isPaidPressRelease}
                paragraphIndices={paragraphIndices}
                isFullContent={isFullContent}
                paragraphWidth="md:max-w-[760px]"
              />
            </div>

            <div className={cn("mx-auto w-full px-0 md:max-w-[760px] md:px-3")}>
              <>
                {isFullContent ? (
                  <ArticleTags
                    tags={article?.tags}
                    className="mb-6 md:px-6"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}
              </>

              <>
                {publishTime && isFullContent ? (
                  <div className="no-print mb-6 border-t border-gray-175 py-3 md:mx-6">
                    <ArticlePurchaseLink
                      title={title}
                      publishedDate={publishTime}
                      versions={Versions.version2}
                      displayPurchase={purchaseArticleDetails.isDisplayed}
                      urlPath={urlPath}
                    />
                  </div>
                ) : null}
              </>

              <PromoteBTGController />

              <>
                {isSGUser ? (
                  <PosthogFeature flagName="feature_enable_user_login">
                    <DelayedComponentsLocationChange
                      waitBeforeShow={1000}
                      urlPath={urlPath}
                    >
                      <CASBlock
                        cdpId={cdpPaywallModuleId}
                        index={index || 0}
                        className="no-print mb-6 mt-11 lg:-mx-6"
                        fallback={
                          !isSubscriber ? (
                            <FallbackPaywall
                              className="mb-6 transition duration-300 ease-in data-[closed]:opacity-0"
                              isPremium={isPremium}
                            />
                          ) : null
                        }
                      />
                    </DelayedComponentsLocationChange>
                  </PosthogFeature>
                ) : null}
              </>

              <>
                {!isFullContent ? (
                  <ArticleTags
                    tags={article.tags}
                    className="mb-8 mt-11 lg:-mx-6"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}

                <PosthogFeature
                  flagName={FEATURE_FLAG_DEEPERDIVE_AB_TEST}
                  variantKey={
                    isBypassEnabled
                      ? undefined
                      : FEATURE_FLAG_DEEPERDIVE_AB_TEST_VARIANT
                  }
                >
                  <Deeperdive
                    urlPath={article.urlPath}
                    counter={index}
                    isArticlePage
                    gaVariant={gaVariant}
                  />
                </PosthogFeature>

                {mainSection && !isPaidPressRelease ? (
                  <DynamicImu
                    index={index || 0}
                    totalParagraphs={totalParagraphs}
                    adsWrapperClass={cn(
                      "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                      { "md:!my-12": IS_PLAYWRIGHT }
                    )}
                    articleId={article?.id}
                    keywords={article?.tags}
                  />
                ) : null}
              </>
            </div>

            {trendingCards && trendingCards.length > 0 ? (
              <Container rootClassName="px-0 no-print">
                <TrendingCards
                  trendingCards={trendingCards}
                  currentArticleId={id}
                  urlPath={urlPath}
                />
              </Container>
            ) : null}

            {isTaboolaEnabled ? (
              <Container rootClassName="px-0 no-print">
                <Taboola
                  container={`below-article-thumbnails-${index}`}
                  placement={`Below Article Thumbnails-${index}`}
                  url={article.urlPath}
                  pageType="article"
                />
              </Container>
            ) : null}
          </div>
        </div>
      </article>
    </>
  );
};

export default InfographicNews;
